import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { getExternalProviders, startExternalRecovery } from '@/api/externalAuth.api';
import AuthContainer from '@/components/Container/AuthContainer';
import { Button } from '@/components/UI/Button';
import type { ExternalProviderDescriptor } from '@/types/externalAuth';
import { errorHandler } from '@/utils/toastHandler';
import { ActionRow, Intro, ProviderActions, StatusMessage } from '../externalStyles';

const recoveryTokenFromFragment = (hash: string): string => {
  const fragment = hash.startsWith('#') ? hash.slice(1) : '';
  const token = new URLSearchParams(fragment).get('token') || '';
  return /^[a-f0-9]{64}$/i.test(token) ? token : '';
};

const ExternalRecovery = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [token] = useState(() => recoveryTokenFromFragment(window.location.hash));
  const [providers, setProviders] = useState<ExternalProviderDescriptor[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [providerLoadFailed, setProviderLoadFailed] = useState(false);
  const locale = useMemo(() => (['en', 'tc', 'sc'].includes(i18n.language) ? i18n.language : 'en') as 'en' | 'tc' | 'sc', [i18n.language]);

  useEffect(() => {
    // Clear the fragment only after React has captured it. Keeping the state
    // initializer pure also makes this reliable under React Strict Mode.
    window.history.replaceState({}, document.title, window.location.pathname);
  }, []);

  useEffect(() => {
    if (!token || process.env.REACT_APP_EXTERNAL_AUTH_ENABLED !== 'true') {
      setLoadingProviders(false);
      return;
    }
    getExternalProviders()
      .then((response) => setProviders(response.data.data.providers.filter((provider) => provider.enabled_for_reauthentication)))
      .catch(() => {
        setProviderLoadFailed(true);
        errorHandler(t('pages.auth.external.recoveryUnavailable'));
      })
      .finally(() => setLoadingProviders(false));
  }, [t, token]);

  const begin = async (provider: string) => {
    setLoading(true);
    try {
      const response = await startExternalRecovery(provider, token);
      window.location.assign(response.data.data.authorization_url);
    } catch (error: any) {
      errorHandler(error.response?.data?.data?.code || error.response?.data?.message || t('pages.auth.external.recoveryUnavailable'));
      setLoading(false);
    }
  };

  const available = Boolean(token) && process.env.REACT_APP_EXTERNAL_AUTH_ENABLED === 'true';

  return <AuthContainer title={t('pages.auth.external.recoveryTitle')}>
    {available && <Intro>{t('pages.auth.external.recoveryInstructions')}</Intro>}
    <div aria-live="polite">
      {loadingProviders && <StatusMessage tone="info" role="status">{t('pages.auth.external.loadingProviders')}</StatusMessage>}
      {!available && <StatusMessage tone="error" role="alert">{t('pages.auth.external.recoveryInvalid')}</StatusMessage>}
      {providerLoadFailed && <StatusMessage tone="error" role="alert">{t('pages.auth.external.recoveryUnavailable')}</StatusMessage>}
      {available && !loadingProviders && !providerLoadFailed && providers.length === 0
        && <StatusMessage tone="info">{t('pages.auth.external.noRecoveryProvider')}</StatusMessage>}
    </div>
    {available && providers.length > 0 && <ProviderActions>
      {providers.map((provider) => <Button key={provider.key} type="button" size="lg" variant="primary" rounded={false}
        disabled={loading} onClick={() => begin(provider.key)}>
        {loading
          ? t('pages.auth.external.redirecting')
          : t('pages.auth.external.recoverWith', { provider: provider.display_name[locale] || provider.display_name.en || provider.key })}
      </Button>)}
    </ProviderActions>}
    {(!available || providerLoadFailed) && <ActionRow>
      <Button type="button" size="lg" variant="primary" rounded={false} onClick={() => navigate('/auth/signin')}>
        {t('pages.auth.external.backToSignIn')}
      </Button>
    </ActionRow>}
  </AuthContainer>;
};

export default ExternalRecovery;
